import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  AlertTriangle,
  Bell,
  CalendarClock,
  Check,
  FileText,
  Hash,
  Mail,
  MapPin,
  MessageCircle,
  Pencil,
  Phone,
  TrendingUp,
  UserRound,
  UserPlus,
  WalletCards,
  X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { formatDoctorName } from "@/lib/doctorName";
import { useAuth } from "@/context/AuthContext";
import PatientTabs from "@/components/patients/PatientTabs";
import SeniorPwdTagControl from "@/components/patients/SeniorPwdTagControl";
import OwningDentistSection from "@/components/patient/OwningDentistSection";
import {
  PATIENT_OVERVIEW_DEMO,
  PATIENT_OVERVIEW_DEMO_ID,
} from "@/tutorials/patientOverviewDemo";
import {
  ORTHO_TRANSFER_DEMO,
  ORTHO_TRANSFER_DEMO_ID,
} from "@/tutorials/orthoTransferDemo";

// =====================================================================
// PatientDetail — Phase 5 R2B shell.
// Header (avatar / name / status / med-history badge / contact row /
// recall toggle) + 10-tab bar. Overview..Files = placeholder. History
// is wired to dos_v2.list_patient_history.
// =====================================================================

const STATUS_TONES = {
  New:      "bg-violet-50 text-violet-700",
  Active:   "bg-emerald-50 text-emerald-700",
  Inactive: "bg-slate-100 text-slate-600",
  Archived: "bg-rose-50 text-rose-700",
};
const PCM_LABELS = {
  no_preference: "Not set",
  phone:         "Phone call",
  sms:           "SMS",
  email:         "Email",
};

function formatMoney(centavos, currency) {
  const symbol = currency === "PHP" ? "₱"
              : currency === "USD" ? "$"
              : currency === "INR" ? "₹"
              : currency ? `${currency} ` : "";
  return symbol + Math.round((centavos ?? 0) / 100).toLocaleString("en-US");
}

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeClinic, clinicRole } = useAuth();
  const demoQuery = new URLSearchParams(location.search);
  const tutorialId = demoQuery.get("tutorial");
  const patientOverviewDemoMode =
    id === PATIENT_OVERVIEW_DEMO_ID &&
    tutorialId === "patient-overview" &&
    demoQuery.get("demo") === "1";
  const orthoTransferDemoMode =
    id === ORTHO_TRANSFER_DEMO_ID &&
    tutorialId === "ortho-transfer-case" &&
    demoQuery.get("demo") === "1";
  const tutorialDemoMode = patientOverviewDemoMode || orthoTransferDemoMode;
  const tutorialDemoData = orthoTransferDemoMode ? ORTHO_TRANSFER_DEMO : PATIENT_OVERVIEW_DEMO;
  const [patient, setPatient] = useState(null);
  const [ltvSummary, setLtvSummary] = useState(null);
  const [txPlanAlerts, setTxPlanAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const canEditContact     = !tutorialDemoMode && clinicRole && clinicRole !== "assistant";
  const canToggleRecall    = !tutorialDemoMode && clinicRole && clinicRole !== "assistant";
  // Senior/PWD legal-status tag — clinic_admin + doctor only.
  // Receptionists handle per-visit discounts at checkout, but the
  // legal-status tag itself is a clinical record.
  const canEditTag         = !tutorialDemoMode && (clinicRole === "clinic_admin" || clinicRole === "doctor");
  const currency           = activeClinic?.currency || "PHP";

  const load = useCallback(async () => {
    if (tutorialDemoMode) {
      setPatient(tutorialDemoData.patient);
      setLtvSummary(tutorialDemoData.ltvSummary);
      setTxPlanAlerts(tutorialDemoData.txPlanAlerts);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const [
      { data, error: err },
      { data: ltvData, error: ltvErr },
      { data: alertData, error: alertErr },
    ] = await Promise.all([
      supabase.rpc("get_patient", { p_id: id }),
      supabase.rpc("get_patient_ltv_summary", { p_patient_id: id }),
      supabase.rpc("get_patient_tx_plan_alerts", { p_patient_id: id }),
    ]);
    setLoading(false);
    if (err) { setError(err.message ?? "Could not load patient."); return; }
    if (ltvErr) { setError(ltvErr.message ?? "Could not load patient LTV."); return; }
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) { setError("Patient not found."); return; }
    setPatient(row);
    setLtvSummary(Array.isArray(ltvData) ? (ltvData[0] || null) : ltvData);
    if (!alertErr) setTxPlanAlerts(Array.isArray(alertData) ? alertData : []);
  }, [id, tutorialDemoData, tutorialDemoMode]);

  useEffect(() => { load(); }, [load]);

  if (loading && !patient) {
    return <div data-testid="patient-detail-loading" className="text-sm text-slate-400">Loading patient…</div>;
  }
  if (error || !patient) {
    return (
      <div className="space-y-4">
        <BackLink onClick={() => navigate("/patients")} />
        <div data-testid="patient-detail-not-found" className="bg-white rounded-2xl shadow-sm p-10 text-center">
          <div className="w-12 h-12 rounded-full bg-rose-50 mx-auto flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-rose-600" /></div>
          <h2 className="mt-4 text-base font-semibold text-slate-900">Patient not found</h2>
          <p className="text-sm text-slate-500 mt-1">{error ?? "The record may have been deleted or moved to another clinic."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="patient-detail-page">
      <BackLink onClick={() => navigate("/patients")} />
      <span data-testid="patient-detail-id" className="hidden">{id}</span>

      <PatientHeader
        patient={patient}
        ltvSummary={ltvSummary}
        currency={currency}
        txPlanAlerts={txPlanAlerts}
        canEditContact={canEditContact}
        canToggleRecall={canToggleRecall}
        canEditTag={canEditTag}
        onOpenPlan={(planId) => navigate(`/patients/${patient.id}?tab=treatment&plan=${planId}`)}
        onChanged={load}
      />

      {orthoTransferDemoMode ? null : <OwningDentistSection patient={patient} onChanged={load} />}

      <PatientTabs
        patientId={patient.id}
        patient={patient}
        tutorialDemoData={tutorialDemoMode ? tutorialDemoData : null}
      />
    </div>
  );
}

function BackLink({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="patient-detail-back"
      className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
    >
      <ArrowLeft className="w-4 h-4" /> Back to Patients
    </button>
  );
}

// ---- Header card ---------------------------------------------------
function PatientHeader({ patient, ltvSummary, currency, txPlanAlerts = [], canEditContact, canToggleRecall, canEditTag, onOpenPlan, onChanged }) {
  const balance = patient.balance_centavos ?? 0;
  const ltvCurrency = ltvSummary?.currency || currency;
  const initial = (patient.full_name?.trim()?.[0] || "?").toUpperCase();
  const hasPendingTreatment = Number(patient.pending_treatment_centavos || 0) > 0;
  const emergencyContact = [
    patient.emergency_contact_name,
    patient.emergency_contact_phone,
  ].filter(Boolean).join(" · ");

  async function patchContact(changes, successMessage = "Contact updated") {
    const { error } = await supabase.rpc("update_patient_contact", { p_id: patient.id, p_changes: changes });
    if (error) { toast.error(error.message ?? "Could not update."); return false; }
    toast.success(successMessage);
    onChanged?.();
    return true;
  }

  async function patchMessengerUrl(url) {
    const { error } = await supabase.rpc("update_patient_messenger_url", {
      p_patient_id: patient.id,
      p_url:        url || null,
    });
    if (error) { toast.error(error.message ?? "Could not update Messenger URL."); return false; }
    toast.success(url ? "Messenger URL saved" : "Messenger URL cleared");
    onChanged?.();
    return true;
  }

  async function verifySelfRegistration() {
    const { error } = await supabase.rpc("verify_self_registered_patient", { p_patient_id: patient.id });
    if (error) { toast.error(error.message ?? "Could not verify patient."); return; }
    toast.success("Patient verified");
    onChanged?.();
  }

  return (
    <section className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="patient-detail-header" data-tutorial-id="patient-detail-header">
      <div className="p-5 sm:p-6">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 min-w-0">
          <div className="flex gap-5 min-w-0 flex-1">
            <div className="w-16 h-16 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center text-xl font-bold shrink-0" data-testid="patient-detail-avatar">
              {initial}
            </div>

            <div className="flex-1 min-w-0">
              <div className="space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <InlinePatientName
                    value={patient.full_name}
                    disabled={!canEditContact}
                    onSave={(v) => patchContact({ full_name: v }, "Profile updated")}
                  />
                  <span data-testid="patient-detail-status" className={`text-[11px] uppercase tracking-wider font-semibold px-2.5 py-0.5 rounded-full ${STATUS_TONES[patient.status] || STATUS_TONES.New}`}>{patient.status}</span>
                  {patient.registration_source === "self_registration" && patient.self_registered_verified === false ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span data-testid="patient-detail-selfreg-badge" className="text-[11px] uppercase tracking-wider font-semibold px-2.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 inline-flex items-center gap-1">
                        <UserPlus className="w-3 h-3" /> Self-registered
                      </span>
                      {canEditContact ? (
                        <button
                          type="button"
                          onClick={verifySelfRegistration}
                          data-testid="patient-detail-verify-selfreg"
                          className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"
                        >
                          <Check className="w-3 h-3" /> Mark verified
                        </button>
                      ) : null}
                    </span>
                  ) : null}
                  {patient.recall_status === "Priority" ? (
                    <span data-testid="patient-detail-recall-priority" className="text-[11px] uppercase tracking-wider font-semibold px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">Recall Priority</span>
                  ) : null}
                  {!patient.has_medical_history ? (
                    <span data-testid="patient-detail-medhistory-badge" className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Medical history never submitted
                    </span>
                  ) : null}
                  <SeniorPwdTagControl
                    patient={patient}
                    canEdit={canEditTag}
                    onChanged={onChanged}
                    testIdPrefix="patient-detail"
                  />
                </div>

                <TxPlanContinuityAlerts
                  alerts={txPlanAlerts}
                  currency={currency}
                  onOpenPlan={onOpenPlan}
                />
              </div>
            </div>
          </div>

          <div className="lg:w-[480px] shrink-0" data-testid="patient-detail-stats">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div title="Lifetime confirmed collections, net of refunds and reversed payments.">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  <TrendingUp className="w-3.5 h-3.5" /> LTV
                </div>
                <div className="text-xl font-bold leading-none mt-1.5 text-slate-900" data-testid="patient-detail-ltv">
                  {formatMoney(ltvSummary?.lifetime_collected_centavos, ltvCurrency)}
                </div>
                <LtvSubline summary={ltvSummary} />
              </div>

              <div>
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  <FileText className="w-3.5 h-3.5" /> Pending Treatment
                </div>
                <div className={`text-xl font-bold leading-none mt-1.5 ${hasPendingTreatment ? "text-amber-600" : "text-slate-900"}`} data-testid="patient-detail-otv">
                  {formatMoney(patient.pending_treatment_centavos, currency)}
                </div>
                <PendingTreatmentSubline
                  currentPhase={patient.current_phase}
                  totalPhases={patient.total_phases}
                  lastVisitDate={patient.last_visit_date}
                />
              </div>

              <div>
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  <WalletCards className="w-3.5 h-3.5" /> Balance
                </div>
                <div data-testid="patient-detail-balance" className={`text-xl font-bold mt-1.5 leading-none ${balance > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                  {formatMoney(balance, currency)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-4 border-t border-slate-100 pt-4">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Patient details</div>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              <HeaderDetailRow icon={Phone} label="Phone" testId="patient-detail-contact-row">
                <InlineText
                  value={patient.phone}
                  placeholder="Add phone"
                  emptyLabel="No phone"
                  disabled={!canEditContact}
                  onSave={(v) => patchContact({ phone: v })}
                  testid="phone"
                  required
                />
              </HeaderDetailRow>

              <HeaderDetailRow icon={Mail} label="Email">
                <InlineText
                  value={patient.email}
                  placeholder="Add email"
                  emptyLabel="Add email"
                  disabled={!canEditContact}
                  onSave={(v) => patchContact({ email: v })}
                  testid="email"
                  type="email"
                />
              </HeaderDetailRow>

              <HeaderDetailRow icon={MessageCircle} label="Messenger" testId="patient-detail-messenger-row">
                <InlineText
                  value={patient.messenger_conversation_url}
                  placeholder="https://business.facebook.com/latest/inbox/…"
                  emptyLabel="No Messenger URL"
                  disabled={!canEditContact}
                  onSave={patchMessengerUrl}
                  testid="messenger-url"
                  type="url"
                />
              </HeaderDetailRow>

              <HeaderDetailRow icon={UserRound} label="Emergency contact">
                <span className={emergencyContact ? "text-slate-700" : "italic text-slate-400"}>
                  {emergencyContact || "Not recorded"}
                </span>
              </HeaderDetailRow>

              <HeaderDetailRow icon={CalendarClock} label="DOB">
                <InlineText
                  value={patient.date_of_birth || ""}
                  displayValue={formatPatientDate(patient.date_of_birth)}
                  placeholder="YYYY-MM-DD"
                  emptyLabel="Add DOB"
                  disabled={!canEditContact}
                  onSave={(v) => patchContact({ date_of_birth: v || null })}
                  testid="dob"
                  type="date"
                />
              </HeaderDetailRow>

              {patient.address ? (
              <HeaderDetailRow icon={MapPin} label="Address">
                <span className={patient.address ? "text-slate-700" : "italic text-slate-400"}>
                  {patient.address || "Not recorded"}
                </span>
              </HeaderDetailRow>
              ) : null}
            </div>
          </div>

          <div className="min-w-0 xl:border-l xl:border-slate-100 xl:pl-6">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Clinic details</div>
            <div className="mt-2 grid grid-cols-1 gap-y-1.5 text-sm">
              <HeaderDetailRow icon={UserRound} label="Preferred doctor">
                <span data-testid="patient-detail-doctor" className={patient.preferred_doctor_name ? "text-slate-700" : "italic text-slate-400"}>
                  {patient.preferred_doctor_name ? formatDoctorName(patient.preferred_doctor_name) : "No preferred doctor"}
                </span>
              </HeaderDetailRow>

              <HeaderDetailRow icon={Hash} label="Patient number">
                <span className={patient.patient_number ? "text-slate-700" : "italic text-slate-400"}>
                  {patient.patient_number || "Not assigned"}
                </span>
              </HeaderDetailRow>

              <PreferredContactMethod
                value={patient.preferred_contact_method}
                disabled={!canEditContact}
                onSave={(v) => patchContact({ preferred_contact_method: v })}
              />

              <RecallOptOutControl
                optedIn={patient.recall_opt_in}
                disabled={!canToggleRecall}
                onToggle={async (optOut, reason) => {
                  const { error } = await supabase.rpc("toggle_recall_opt_out", { p_id: patient.id, p_opt_out: optOut, p_reason: reason || null });
                  if (error) { toast.error(error.message ?? "Could not update."); return false; }
                  toast.success(optOut ? "Patient opted out of recall" : "Recall outreach allowed");
                  onChanged?.();
                  return true;
                }}
              />

              {patient.last_visit_date ? (
                <HeaderDetailRow icon={CalendarClock} label="Last seen">
                  <span className="text-slate-700">{formatRelativeDays(patient.last_visit_date)}</span>
                </HeaderDetailRow>
              ) : null}
            </div>
          </div>
        </div>
      </div>

    </section>
  );
}

function HeaderDetailRow({ icon: Icon, label, children, testId }) {
  return (
    <div className="flex items-center gap-2 min-w-0 text-sm" data-testid={testId}>
      <Icon className="h-4 w-4 shrink-0 text-slate-400" />
      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</span>
      <span className="min-w-0 text-slate-600">{children}</span>
    </div>
  );
}

function TxPlanContinuityAlerts({ alerts, currency, onOpenPlan }) {
  if (!alerts?.length) return null;
  return (
    <div
      data-testid="patient-tx-plan-alerts"
      className="flex flex-wrap gap-2"
    >
      {alerts.map((a) => (
        <button
          key={a.plan_id}
          type="button"
          onClick={() => onOpenPlan?.(a.plan_id)}
          className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-[12px] font-semibold text-amber-900 hover:bg-amber-100"
          data-testid={`patient-tx-plan-alert-${a.pipeline_state || a.status}`}
          title="Open treatment plan"
        >
          <FileText className="w-3.5 h-3.5" strokeWidth={2.25} />
          <span>{a.status}</span>
          <span className="text-amber-700">·</span>
          <span className="tabular-nums">{formatMoney(a.total_centavos, currency)}</span>
        </button>
      ))}
    </div>
  );
}

// PendingTreatmentSubline — context line below the headline number.
// Renders pieces only when meaningful: phase pair only when both
// current and total are present (and item-level phasing was actually
// used), last-seen only when there is a finalized visit on file.
// Empty when neither is true so the slot collapses cleanly.
function PendingTreatmentSubline({ currentPhase, totalPhases, lastVisitDate }) {
  const hasPhases  = currentPhase && totalPhases && totalPhases > 0;
  const lastSeen   = lastVisitDate ? formatRelativeDays(lastVisitDate) : null;
  if (!hasPhases && !lastSeen) return null;

  return (
    <p className="text-[11px] text-slate-500 mt-1 leading-tight" data-testid="patient-detail-pending-subline">
      {hasPhases ? (
        <span data-testid="patient-detail-phase">Phase {currentPhase} of {totalPhases}</span>
      ) : null}
      {hasPhases && lastSeen ? <span className="text-slate-300"> · </span> : null}
      {lastSeen ? (
        <span data-testid="patient-detail-last-seen">last seen {lastSeen}</span>
      ) : null}
    </p>
  );
}

function LtvSubline({ summary }) {
  const count = Number(summary?.confirmed_payment_count || 0);
  const lastPaid = summary?.last_payment_date ? formatRelativeDays(summary.last_payment_date) : null;
  if (count <= 0) {
    return (
      <p className="text-[11px] text-slate-500 mt-1 leading-tight" data-testid="patient-detail-ltv-subline">
        no confirmed payments
      </p>
    );
  }
  return (
    <p className="text-[11px] text-slate-500 mt-1 leading-tight" data-testid="patient-detail-ltv-subline">
      <span>{count} confirmed payment{count === 1 ? "" : "s"}</span>
      {lastPaid ? <span className="text-slate-300"> · </span> : null}
      {lastPaid ? <span>last paid {lastPaid}</span> : null}
    </p>
  );
}

function formatRelativeDays(isoDate) {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  // Compare in local-day buckets (not raw ms) so "yesterday" doesn't
  // flip to "today" depending on what hour the visit was finalized at.
  const days = Math.max(0, Math.floor(
    (Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()) -
     Date.UTC(d.getFullYear(),     d.getMonth(),     d.getDate())) / 86400000
  ));
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30)  return `${days}d ago`;
  if (days < 60)  return "1mo ago";
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  const years = Math.floor(days / 365);
  return years === 1 ? "1yr ago" : `${years}yr ago`;
}

function formatPatientDate(isoDate) {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ---- Inline name editor --------------------------------------------
function InlinePatientName({ value, disabled, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value ?? "");
  const [busy, setBusy]       = useState(false);
  const lastPressRef          = useRef(0);

  useEffect(() => { setDraft(value ?? ""); }, [value]);

  function openEditor() {
    if (!disabled) setEditing(true);
  }

  function handlePress() {
    if (disabled) return;
    const now = Date.now();
    if (now - lastPressRef.current <= 400) {
      lastPressRef.current = 0;
      openEditor();
      return;
    }
    lastPressRef.current = now;
  }

  async function save() {
    if (busy) return;
    const trimmed = draft.trim();
    if (!trimmed) { toast.error("Full name is required."); return; }
    if (trimmed === (value ?? "")) { setEditing(false); return; }
    setBusy(true);
    const ok = await onSave(trimmed);
    setBusy(false);
    if (ok) setEditing(false);
  }

  if (editing) {
    return (
      <h1 className="flex min-w-0 max-w-full items-center gap-1.5" data-testid="patient-detail-name">
        <input
          type="text"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter")  { e.preventDefault(); save(); }
            if (e.key === "Escape") { e.preventDefault(); setDraft(value ?? ""); setEditing(false); }
          }}
          data-testid="patient-inline-name-input"
          aria-label="Patient full name"
          className="w-full min-w-[14rem] max-w-sm rounded-lg border border-slate-200 px-2.5 py-1 text-2xl font-bold leading-tight tracking-tight text-slate-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
        />
        <button type="button" onClick={save} disabled={busy} data-testid="patient-inline-name-save" className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 disabled:opacity-60">
          <Check className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => { setDraft(value ?? ""); setEditing(false); }} disabled={busy} data-testid="patient-inline-name-cancel" className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 disabled:opacity-60">
          <XIcon className="w-4 h-4" />
        </button>
      </h1>
    );
  }

  const displayName = value || "Unnamed patient";
  return (
    <h1
      className={`text-2xl font-bold text-slate-900 tracking-tight leading-tight rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500/40 ${disabled ? "" : "cursor-pointer select-none hover:text-slate-700"}`}
      data-testid="patient-detail-name"
      title={disabled ? undefined : "Double click to edit name"}
      role={disabled ? undefined : "button"}
      tabIndex={disabled ? undefined : 0}
      onClick={handlePress}
      onDoubleClick={(e) => { e.preventDefault(); openEditor(); }}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          openEditor();
        }
      }}
    >
      {displayName}
    </h1>
  );
}

// ---- Inline text editor (phone, email) -----------------------------
function InlineText({ value, displayValue, placeholder, emptyLabel, disabled, onSave, testid, type = "text", required = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value ?? "");
  const [busy, setBusy]       = useState(false);

  useEffect(() => { setDraft(value ?? ""); }, [value]);

  if (!editing) {
    const display = value ? (displayValue || value) : <span className="italic text-slate-400">{emptyLabel}</span>;
    return (
      <button
        type="button"
        onClick={() => !disabled && setEditing(true)}
        disabled={disabled}
        data-testid={`patient-inline-${testid}-display`}
        title={disabled ? "Your role can't edit contact details" : "Click to edit"}
        className={`inline-flex items-center gap-1 text-slate-600 ${disabled ? "cursor-not-allowed" : "hover:text-slate-900"}`}
      >
        <span>{display}</span>
        {!disabled ? <Pencil className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100" /> : null}
      </button>
    );
  }

  async function save() {
    if (busy) return;
    const trimmed = draft.trim();
    if (required && !trimmed) { toast.error("This field is required."); return; }
    if (trimmed === (value ?? "")) { setEditing(false); return; }
    setBusy(true);
    const ok = await onSave(trimmed);
    setBusy(false);
    if (ok) setEditing(false);
  }

  return (
    <span className="inline-flex items-center gap-1">
      <input
        type={type}
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter")   { e.preventDefault(); save(); }
          if (e.key === "Escape")  { e.preventDefault(); setDraft(value ?? ""); setEditing(false); }
        }}
        data-testid={`patient-inline-${testid}-input`}
        placeholder={placeholder}
        className="px-2 py-1 text-sm rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 min-w-[10ch]"
      />
      <button type="button" onClick={save} disabled={busy} data-testid={`patient-inline-${testid}-save`} className="p-1 rounded text-emerald-600 hover:bg-emerald-50">
        <Check className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={() => { setDraft(value ?? ""); setEditing(false); }} disabled={busy} data-testid={`patient-inline-${testid}-cancel`} className="p-1 rounded text-slate-400 hover:bg-slate-100">
        <XIcon className="w-3.5 h-3.5" />
      </button>
    </span>
  );
}

// ---- Preferred contact method --------------------------------------
function PreferredContactMethod({ value, disabled, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value || "no_preference");
  const [busy, setBusy]       = useState(false);

  useEffect(() => { setDraft(value || "no_preference"); }, [value]);

  if (!editing) {
    return (
      <div className="text-sm text-slate-500" data-testid="patient-detail-pcm-row">
        Preferred contact method: <span className="font-semibold text-slate-700" data-testid="patient-detail-pcm-display">{PCM_LABELS[value] || "Not set"}</span>
        {!disabled ? (
          <button type="button" onClick={() => setEditing(true)} data-testid="patient-detail-pcm-edit" className="ml-2 inline-flex items-center text-slate-400 hover:text-slate-700">
            <Pencil className="w-3 h-3" />
          </button>
        ) : null}
      </div>
    );
  }

  async function save() {
    if (busy) return;
    if (draft === value) { setEditing(false); return; }
    setBusy(true);
    const ok = await onSave(draft);
    setBusy(false);
    if (ok) setEditing(false);
  }

  return (
    <div className="flex items-center gap-2" data-testid="patient-detail-pcm-editor">
      <select
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        autoFocus
        data-testid="patient-detail-pcm-select"
        className="px-2 py-1 text-sm rounded border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
      >
        <option value="no_preference">No preference</option>
        <option value="phone">Phone call</option>
        <option value="sms">SMS</option>
        <option value="email">Email</option>
      </select>
      <button type="button" onClick={save} disabled={busy} data-testid="patient-detail-pcm-save" className="p-1 rounded text-emerald-600 hover:bg-emerald-50">
        <Check className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={() => { setDraft(value || "no_preference"); setEditing(false); }} disabled={busy} data-testid="patient-detail-pcm-cancel" className="p-1 rounded text-slate-400 hover:bg-slate-100">
        <XIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ---- Recall opt-out control ----------------------------------------
function RecallOptOutControl({ optedIn, disabled, onToggle }) {
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason]         = useState("");
  const [busy, setBusy]             = useState(false);

  async function toggle(optOut) {
    setBusy(true);
    const ok = await onToggle(optOut, reason.trim());
    setBusy(false);
    if (ok) { setReasonOpen(false); setReason(""); }
  }

  return (
    <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="patient-detail-recall-row">
      <Bell className="w-3.5 h-3.5 text-slate-400" />
      Recall outreach: <span className="font-semibold text-slate-700" data-testid="patient-detail-recall-state">{optedIn ? "allowed" : "opted out"}</span>
      {!disabled ? (
        optedIn ? (
          <button type="button" onClick={() => setReasonOpen(true)} data-testid="patient-detail-recall-optout-button" className="text-xs text-slate-500 underline-offset-2 hover:underline">
            Opt out
          </button>
        ) : (
          <button type="button" onClick={() => toggle(false)} data-testid="patient-detail-recall-allow-button" className="text-xs text-slate-500 underline-offset-2 hover:underline">
            Allow recall again
          </button>
        )
      ) : null}

      {reasonOpen ? (
        <div role="dialog" aria-modal="true" data-testid="recall-reason-dialog" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setReasonOpen(false); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-bold text-slate-900">Opt out of recall outreach?</h3>
            <p className="text-sm text-slate-500">The patient won't be queued for follow-up. Optionally capture why.</p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="recall-reason-input"
              rows={3}
              placeholder="Reason (optional)"
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
            />
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => { setReasonOpen(false); setReason(""); }} data-testid="recall-reason-cancel" className="px-3.5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={() => toggle(true)} disabled={busy} data-testid="recall-reason-confirm" className="px-4 py-2 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60">{busy ? "Saving…" : "Confirm opt-out"}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
